import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Space, 
  Typography, 
  Row, 
  Col, 
  Alert, 
  message,
  Drawer,
  Tag,
  Divider,
  Spin
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined,
  FilterOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../hooks/storeContext';
// AdminLayout now provided at router level
import AuditService from '../../services/AuditService';
import api from '../../services/api';
import { COLORS } from '../../constants/colors';
import FilterDrawer from './components/FilterDrawer';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  ActionButton,
  FilterContainer,
  StyledTable,
  StyledTableContainer,
  StyledInput,
  ActionTag,
  ResourceTag,
  SummaryCard,
  PaginationText
} from './styled';

const { Text } = Typography;

const AuditLogsPageComponent = () => {
  const { authStore } = useStore();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    resource: 'all',
    action: 'all',
    dateRange: null,
    search: '',
    userId: undefined
  });
  const [summary, setSummary] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [expandedChanges, setExpandedChanges] = useState({});
  const [users, setUsers] = useState([]);
  const { setPageHeader } = usePageHeader();
  const searchTimerRef = useRef(null);

  // Fetch users for filter dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users', { limit: '200', status: 'active' });
        if (response?.data) {
          setUsers(response.data);
        }
      } catch (e) {
        // Non-critical — filter just won't have user options
      }
    };
    fetchUsers();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
        resource: filters.resource !== 'all' ? filters.resource : undefined,
        action: filters.action !== 'all' ? filters.action : undefined,
        userId: filters.userId || undefined,
        search: filters.search || undefined
      };
      const response = await AuditService.exportLogs(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Audit logs exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  // Set page header with dynamic count
  useEffect(() => {
    setPageHeader({
      title: 'Logs',
      count: totalLogs,
      loading: loading,
      loadingType: 'heart'
    });
  }, [setPageHeader, totalLogs, loading]);

  // Fetch audit data from API
  const fetchAuditLogs = useCallback(async (paginationOverride) => {
    setLoading(true);
    try {
      const pg = paginationOverride || pagination;
      const actionFilter = filters.action !== 'all' ? filters.action : undefined;
      const resourceFilter = filters.resource !== 'all' ? filters.resource : undefined;
      const response = await AuditService.fetchAudit({
        resource: resourceFilter,
        action: actionFilter,
        userId: filters.userId || undefined,
        search: filters.search || undefined,
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        limit: pg.pageSize,
        skip: (pg.current - 1) * pg.pageSize
      });
      // api client returns parsed JSON directly: { data: [...], total, limit, skip }
      // response = { data: logs[], total: N } from backend
      const logs = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const total = response?.total || logs.length;
      setAuditLogs(logs);
      setTotalLogs(total);
      // Basic summary grouping client-side
      const summaryMap = {};
      logs.forEach(log => {
        const key = `${log.action}|${log.resource}`;
        if (!summaryMap[key]) summaryMap[key] = { _id: { action: log.action, resource: log.resource }, count: 0 };
        summaryMap[key].count += 1;
      });
      setSummary(Object.values(summaryMap).slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      message.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  const hasActiveFilters = () => {
    return filters.resource !== 'all' || filters.action !== 'all' || filters.dateRange || filters.userId;
  };

  const loadLogDetail = async (logId) => {
    setSelectedLog(null);
    setDetailVisible(true);
    setDetailLoading(true);
    setExpandedChanges({});
    try {
      const resp = await AuditService.getAuditLogDetail(logId);
      // api client returns parsed JSON directly: { data: { _id, changes, ... } }
      setSelectedLog(resp?.data || null);
    } catch (e) {
      console.error('Audit detail error:', e);
      message.error('Failed to load audit detail');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '15%',
      render: (timestamp) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <Text style={{ fontWeight: 500 }}>{moment(timestamp).format('MMM DD, YYYY')}</Text>
          <Text style={{ fontSize: '12px', color: COLORS.textMuted }}>{moment(timestamp).format('HH:mm:ss')}</Text>
        </div>
      ),
      sorter: (a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'User',
      dataIndex: ['userId'],
      key: 'user',
      width: '20%',
      render: (user) => (
        <Space direction="vertical" size={0}>
          <Text strong>{user?.fullName || 'Unknown'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user?.email}
          </Text>
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '12%',
      render: (action) => {
        const colors = {
          create: 'create',
          update: 'update', 
          delete: 'delete',
          view: 'view',
          match: 'match',
          flag: 'flag'
        };
        return <ActionTag className={colors[action] || 'default'}>{action.toUpperCase()}</ActionTag>;
      },
      filters: [
        { text: 'Create', value: 'create' },
        { text: 'Update', value: 'update' },
        { text: 'Delete', value: 'delete' },
        { text: 'View', value: 'view' },
        { text: 'Match', value: 'match' },
        { text: 'Flag', value: 'flag' }
      ],
      onFilter: (value, record) => record.action === value
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      width: '15%',
      render: (resource, record) => (
        <Space direction="vertical" size={0}>
          <ResourceTag>{resource}</ResourceTag>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            ID: {record.resourceId.substring(0, 8)}...
          </Text>
        </Space>
      ),
      filters: [
        { text: 'Patient', value: 'Patient' },
        { text: 'PatientQueue', value: 'PatientQueue' },
        { text: 'ScreeningRecord', value: 'ScreeningRecord' }
      ],
      onFilter: (value, record) => record.resource === value
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      width: '25%',
      render: (details) => {
        if (!details) return '-';
        
        try {
          const parsed = JSON.parse(details);
          if (parsed.changes) {
            const changeCount = Object.keys(parsed.changes).length;
            return (
              <Space direction="vertical" size={0}>
                <Text strong>{changeCount} field(s) changed</Text>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {Object.keys(parsed.changes).join(', ')}
                </Text>
              </Space>
            );
          }
          if (parsed.action) {
            return <Text>{parsed.action.replace('_', ' ')}</Text>;
          }
          return <Text type="secondary">View details</Text>;
        } catch (e) {
          return <Text type="secondary">{details.substring(0, 50)}...</Text>;
        }
      }
    }
  ];

  if (!authStore.hasPermission('user_management')) {
    return (
        <PageContainer>
          <Alert
            message="Access Denied"
            description="You don't have permission to view audit logs."
            type="error"
            showIcon
            style={{ margin: '24px' }}
          />
        </PageContainer>
    );
  }

  return (
      <PageContainer>
        <div style={{ marginBottom: '16px' }} className="stats-animate-in">
          <Row gutter={[16, 16]}>
            {loading ? (
              // Show 3 skeleton loading cards
              <>
                {[1, 2, 3].map((key) => (
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} key={key}>
                    <SummaryCard style={{ background: '#f5f5f5' }}>
                      <div className="statistic-value" style={{ background: '#e0e0e0', borderRadius: '4px', height: '32px', width: '60px' }}>&nbsp;</div>
                      <div className="statistic-title" style={{ background: '#e0e0e0', borderRadius: '4px', height: '16px', width: '120px', marginTop: '8px' }}>&nbsp;</div>
                    </SummaryCard>
                  </Col>
                ))}
              </>
            ) : (
              // Show actual data cards
              <>
                <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                  <SummaryCard>
                    <div className="statistic-value">{totalLogs}</div>
                    <div className="statistic-title">Total Logs</div>
                  </SummaryCard>
                </Col>
                {summary.slice(0, 2).map((item, index) => (
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} key={index}>
                    <SummaryCard>
                      <div className="statistic-value">{item.count}</div>
                      <div className="statistic-title">{item._id.action} {item._id.resource}</div>
                    </SummaryCard>
                  </Col>
                ))}
              </>
            )}
          </Row>
        </div>

        <div style={{ marginBottom: '16px' }} className="filters-animate-in">
          <FilterContainer>
            <Row gutter={[12, 12]} align="middle">
              <Col flex="auto">
                <StyledInput
                  placeholder="Search user, IP, or details..."
                  prefix={<SearchOutlined style={{ color: COLORS.textMuted, fontSize: '16px' }} />}
                  value={filters.search}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilters(f => ({ ...f, search: val }));
                    // Debounce the actual API call
                    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                    searchTimerRef.current = setTimeout(() => {
                      setPagination(p => ({ ...p, current: 1 }));
                    }, 400);
                  }}
                  style={{ width: '100%' }}
                  allowClear
                />
              </Col>
              
              <Col>
                <ActionButton
                  icon={<FilterOutlined />}
                  onClick={() => setFilterDrawerVisible(true)}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: hasActiveFilters() ? COLORS.$primaryBlue : COLORS.textMedium,
                    borderColor: hasActiveFilters() ? COLORS.$primaryBlue : COLORS.border,
                    background: hasActiveFilters() ? `${COLORS.$primaryBlue}08` : 'white',
                    position: 'relative'
                  }}
                >
                  Filter
                  {hasActiveFilters() && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      background: COLORS.$primaryBlue,
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      border: '2px solid white'
                    }}>
                      {[filters.resource !== 'all', filters.action !== 'all', filters.dateRange].filter(Boolean).length}
                    </span>
                  )}
                </ActionButton>
              </Col>
              
              <Col>
                <ActionButton
                  icon={<ReloadOutlined />}
                  onClick={() => fetchAuditLogs()}
                  loading={loading}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    minWidth: '40px',
                    padding: '0 12px'
                  }}
                />
              </Col>
              
              <Col>
                <ActionButton 
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={handleExport}
                  loading={exporting}
                  style={{ 
                    height: '40px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    backgroundColor: '#000',
                    borderColor: '#000',
                    color: '#fff'
                  }}
                >
                  Export Logs
                </ActionButton>
              </Col>
            </Row>
          </FilterContainer>
        </div>

        <StyledTableContainer className="table-animate-in">
          <StyledTable
            className="event-management-pagination"
            columns={columns}
            dataSource={auditLogs}
            rowKey="_id"
            loading={loading}
            size="middle"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalLogs,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => (
                <PaginationText>
                  Showing {range[0]}-{range[1]} of {total} audit logs
                </PaginationText>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                const newPg = { current: page, pageSize };
                setPagination(newPg);
                fetchAuditLogs(newPg);
              }
            }}
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onClick: () => loadLogDetail(record._id),
              style: { cursor: 'pointer' }
            })}
          />
        </StyledTableContainer>

        <Drawer
          width={560}
          title={selectedLog ? `${selectedLog.action.toUpperCase()} ${selectedLog.resource}` : 'Audit Detail'}
          open={detailVisible}
          onClose={() => { setDetailVisible(false); setSelectedLog(null); }}
          destroyOnClose
          extra={selectedLog && <Tag color="blue">{selectedLog.displaySummary || ''}</Tag>}
        >
          {detailLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            </div>
          )}
          {!detailLoading && selectedLog && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {/* User Info */}
              <div style={{ 
                background: '#f8f9fa', 
                borderRadius: '10px', 
                padding: '14px 16px',
                marginBottom: '12px'
              }}>
                <Text style={{ fontSize: 12, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>User</Text>
                <div style={{ marginTop: 4 }}>
                  <Text strong style={{ fontSize: 14 }}>{selectedLog.user?.fullName || 'Unknown'}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 13 }}>{selectedLog.user?.email}</Text>
                </div>
              </div>

              {/* Metadata Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '10px',
                marginBottom: '12px' 
              }}>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 14px' }}>
                  <Text style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Timestamp</Text>
                  <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>{moment(selectedLog.timestamp).format('MMM DD, YYYY HH:mm:ss')}</div>
                </div>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 14px' }}>
                  <Text style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>IP Address</Text>
                  <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>{formatIP(selectedLog.ipAddress)}</div>
                </div>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 14px' }}>
                  <Text style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Resource</Text>
                  <div style={{ marginTop: 4 }}>
                    <ResourceTag>{selectedLog.resource}</ResourceTag>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{(selectedLog.resourceId || '').substring(0, 10)}…</Text>
                  </div>
                </div>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 14px' }}>
                  <Text style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Integrity Hash</Text>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, marginTop: 4, wordBreak: 'break-all', color: '#555' }}>{(selectedLog.hash || '—').substring(0, 24)}{selectedLog.hash ? '…' : ''}</div>
                </div>
              </div>

              {/* Details / Changes Section */}
              <Divider style={{ margin: '4px 0 12px' }} />
              <div>
                <Text strong style={{ fontSize: 14 }}>{(selectedLog.changes || []).length > 0 ? 'Changes' : 'Details'}</Text>
                {selectedLog.legacy && <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>Legacy</Tag>}
                <div style={{ marginTop: 10 }}>
                  {(selectedLog.changes || []).length === 0 && (
                    <div>
                      {(() => {
                        // Try to parse the details string to show readable info
                        let parsed = null;
                        try { parsed = typeof selectedLog.details === 'string' ? JSON.parse(selectedLog.details) : selectedLog.details; } catch (_) { /* ignore parse errors */ }
                        if (parsed && typeof parsed === 'object') {
                          const entries = Object.entries(parsed).filter(([k]) => !['changes', 'changeCount'].includes(k));
                          if (entries.length > 0) {
                            return (
                              <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                  <thead>
                                    <tr>
                                      <th style={{ padding: '10px 12px', borderBottom: '2px solid #f0f0f0', background: '#fafafa', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', color: COLORS.textMuted, fontWeight: 600 }}>Property</th>
                                      <th style={{ padding: '10px 12px', borderBottom: '2px solid #f0f0f0', background: '#fafafa', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', color: COLORS.textMuted, fontWeight: 600 }}>Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entries.map(([key, val], idx) => (
                                      <tr key={key} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 500, borderBottom: '1px solid #f5f5f5' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</td>
                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f5f5f5', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatValue(val)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          }
                        }
                        if (typeof selectedLog.details === 'string' && selectedLog.details.length > 0) {
                          return (
                            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', fontSize: 13, color: '#555', wordBreak: 'break-word' }}>
                              {selectedLog.details}
                            </div>
                          );
                        }
                        return (
                          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', fontSize: 13, color: '#999' }}>
                            No additional details recorded for this log entry.
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {(selectedLog.changes || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {selectedLog.changes.map((c) => {
                        const oldIsObj = c.old != null && typeof c.old === 'object' && !Array.isArray(c.old) && Object.keys(c.old).length > 0;
                        const newIsObj = c.new != null && typeof c.new === 'object' && !Array.isArray(c.new) && Object.keys(c.new).length > 0;
                        const isComplex = oldIsObj || newIsObj;
                        return (
                          <div key={c.field} style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden' }}>
                            {/* Change Card Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                              <Text strong style={{ fontSize: 13 }}>{c.field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</Text>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {c.classification && <Tag style={{ margin: 0, fontSize: 10 }}>{c.classification}</Tag>}
                                {c.isPHI && <Tag color="red" style={{ margin: 0, fontSize: 10 }}>PHI</Tag>}
                              </div>
                            </div>
                            {/* Change Card Body */}
                            <div style={{ padding: '12px 14px' }}>
                              {!isComplex ? (
                                /* Simple value change: side-by-side before/after */
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13 }}>
                                  <div style={{ flex: 1, padding: '8px 10px', background: '#fff1f0', borderRadius: 6, border: '1px solid #ffccc7' }}>
                                    <div style={{ fontSize: 10, color: '#cf1322', fontWeight: 600, marginBottom: 3 }}>BEFORE</div>
                                    <div style={{ color: '#434343', wordBreak: 'break-word' }}>{formatValue(c.old)}</div>
                                  </div>
                                  <div style={{ paddingTop: 18, color: '#bbb', fontSize: 16 }}>&rarr;</div>
                                  <div style={{ flex: 1, padding: '8px 10px', background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                                    <div style={{ fontSize: 10, color: '#389e0d', fontWeight: 600, marginBottom: 3 }}>AFTER</div>
                                    <div style={{ color: '#434343', wordBreak: 'break-word' }}>{formatValue(c.new)}</div>
                                  </div>
                                </div>
                              ) : (
                                /* Complex object: sub-field diff table */
                                (() => {
                                  const diffs = getObjectDiff(c.old, c.new);
                                  const changed = diffs.filter(d => d.changed);
                                  const unchanged = diffs.filter(d => !d.changed);
                                  const isExpanded = expandedChanges[c.field];
                                  return (
                                    <>
                                      {changed.length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                          <thead>
                                            <tr>
                                              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: COLORS.textMuted, fontWeight: 600, borderBottom: '1px solid #f0f0f0', width: '30%' }}>Property</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#cf1322', fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}>Before</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#389e0d', fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}>After</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {changed.map((d, i) => (
                                              <tr key={d.key} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                                <td style={{ padding: '6px 8px', fontWeight: 500, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', fontSize: 12 }}>
                                                  {d.added && <span style={{ color: '#52c41a', marginRight: 4 }}>+</span>}
                                                  {d.removed && <span style={{ color: '#ff4d4f', marginRight: 4 }}>&minus;</span>}
                                                  {d.key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                                                </td>
                                                <td style={{ padding: '6px 8px', color: d.removed ? '#ff4d4f' : '#999', borderBottom: '1px solid #f5f5f5', wordBreak: 'break-word', maxWidth: 160, fontSize: 12 }}>
                                                  {d.added ? '—' : formatValue(d.oldVal)}
                                                </td>
                                                <td style={{ padding: '6px 8px', color: d.added ? '#52c41a' : '#434343', borderBottom: '1px solid #f5f5f5', wordBreak: 'break-word', maxWidth: 160, fontSize: 12 }}>
                                                  {d.removed ? '—' : formatValue(d.newVal)}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                      {unchanged.length > 0 && (
                                        <div
                                          style={{ marginTop: changed.length > 0 ? 8 : 0, fontSize: 11, color: '#999', cursor: 'pointer', userSelect: 'none' }}
                                          onClick={() => setExpandedChanges(prev => ({ ...prev, [c.field]: !prev[c.field] }))}
                                        >
                                          {isExpanded ? '▾' : '▸'} {unchanged.length} unchanged field{unchanged.length !== 1 ? 's' : ''}
                                        </div>
                                      )}
                                      {isExpanded && unchanged.length > 0 && (
                                        <div style={{ marginTop: 6, padding: '8px 10px', background: '#fafafa', borderRadius: 6, fontSize: 11, color: '#888' }}>
                                          {unchanged.map(d => (
                                            <div key={d.key} style={{ marginBottom: 2 }}>
                                              <span style={{ fontWeight: 500, color: '#666' }}>{d.key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>: {formatValue(d.oldVal)}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* User Agent */}
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 14px' }}>
                <Text style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>User Agent</Text>
                <div style={{ fontSize: 12, color: '#555', marginTop: 4, wordBreak: 'break-word' }}>{selectedLog.userAgent || '—'}</div>
              </div>
            </div>
          )}
        </Drawer>

        <FilterDrawer
          visible={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          filters={filters}
          setFilters={setFilters}
          users={users}
          onApply={() => {
            setPagination(p => ({ ...p, current: 1 }));
            setFilterDrawerVisible(false);
          }}
          onClear={() => {
            setFilters({
              resource: 'all',
              action: 'all',
              dateRange: null,
              search: '',
              userId: undefined
            });
            setPagination(p => ({ ...p, current: 1 }));
            setFilterDrawerVisible(false);
          }}
        />
      </PageContainer>
  );
};

const AuditLogsPage = observer(AuditLogsPageComponent);
export default AuditLogsPage;

function formatValue(v) {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    // Detect ISO date strings and format nicely
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      return moment(v).format('MMM DD, YYYY h:mm A');
    }
    return v.length > 80 ? v.slice(0, 77) + '…' : v;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return 'Empty';
    if (typeof v[0] === 'object' && v[0] !== null) {
      const label = v.length === 1 ? '1 record' : `${v.length} records`;
      const first = v[0];
      const name = first.eventName || first.name || first.visitId || null;
      return name ? `${label} — ${typeof name === 'string' && name.length > 25 ? name.slice(0, 22) + '…' : name}` : label;
    }
    return v.join(', ');
  }
  if (typeof v === 'object') {
    const keys = Object.keys(v).filter(k => k !== '_id' && k !== '__v' && k !== 'id');
    if (keys.length === 0) return 'Empty';
    const preview = keys.slice(0, 3).map(k => {
      const val = v[k];
      const short = val == null ? '—'
        : typeof val === 'string'
          ? (/^\d{4}-\d{2}-\d{2}T/.test(val) ? moment(val).format('MMM DD, YYYY') : (val.length > 20 ? val.slice(0, 17) + '…' : val))
        : typeof val === 'boolean' ? (val ? 'yes' : 'no')
        : Array.isArray(val) ? `[${val.length}]`
        : typeof val === 'object' ? '{…}'
        : String(val);
      return `${k}: ${short}`;
    }).join(', ');
    return keys.length > 3 ? `{ ${preview}, … }` : `{ ${preview} }`;
  }
  return String(v);
}

function getObjectDiff(oldObj, newObj) {
  const old = (oldObj && typeof oldObj === 'object' && !Array.isArray(oldObj)) ? oldObj : {};
  const nw = (newObj && typeof newObj === 'object' && !Array.isArray(newObj)) ? newObj : {};
  const skipKeys = new Set(['_id', '__v', 'id']);
  const allKeys = [...new Set([...Object.keys(old), ...Object.keys(nw)])].filter(k => !skipKeys.has(k));
  return allKeys.map(key => {
    const oldVal = old[key];
    const newVal = nw[key];
    const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
    const added = !(key in old);
    const removed = !(key in nw);
    return { key, oldVal, newVal, changed, added, removed };
  });
}

function formatIP(ip) {
  if (!ip) return '—';
  if (ip === '::1' || ip === '::ffff:127.0.0.1') return '127.0.0.1 (localhost)';
  // Strip IPv6 mapped IPv4 prefix
  if (ip.startsWith('::ffff:')) return ip.replace('::ffff:', '');
  return ip;
}
